import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb.jsx";
import Button from "../../components/button/Button.jsx";
import { BasicModal, LargeModal } from "../../components/modal/ModalUI.jsx";
import { basicModalShowHide, largeModalShowHide, setTitle } from "../../features/modalSlice.js";
import {
  changeQuestPackStatus,
  fetchQuestPackList,
  setDeleteQuestPackId,
  setQuestPackRedirect,
  setSelectedQuestPack,
} from "../../features/questPackSlice.js";
import { questPackDetailsById } from "../../apis/questPack.api.js";
import { getAllCategories } from "../../features/categorySlice.js";
import { fetchSubCategoryDataByCatagoryId } from "../../features/subCategorySlice.js";
import { can } from "../../utils/permission.util.js";
import { errorToastify } from "../../utils/toastify.util.js";
import QuestPackDelete from "./QuestPackDelete.jsx";
import { RichTextDisplay, stripHtml } from "../../components/form/RichTextEditor.jsx";
import { PackStatusBadge } from "../../components/data-table/StatusBadge.jsx";
import { PACK_STATUS_FILTER_OPTIONS } from "../../components/data-table/packStatus.util.js";
import { RowActionButton, RowActionIcons, RowActions } from "../../components/data-table/RowActions.jsx";

const statusOptions = PACK_STATUS_FILTER_OPTIONS;

const LIMIT_OPTIONS = [10, 20, 50, 100];
const getCategoryLabel = (category) => category?.name || category?.category_name || category?.title || `-`;
const getSubCategoryLabel = (subCategory) => subCategory?.name || subCategory?.sub_category_name || subCategory?.title || `-`;
const getDisplayNames = (value, fallback = "-") => {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : fallback;
  }

  if (typeof value === "string") {
    return value.trim() || fallback;
  }

  return fallback;
};

const QuestPackDetailsModal = ({ questPackDetails, isLoading }) => {
  if (isLoading) {
    return <div className="text-center py-3">Loading...</div>;
  }

  if (!questPackDetails) {
    return <div className="text-center py-3 text-muted">No quest pack details found.</div>;
  }

  const statusLabel =
    Number(questPackDetails.status) === 1
      ? "Active"
      : Number(questPackDetails.status) === 3
        ? "Draft"
        : Number(questPackDetails.status) === 0
          ? "Inactive"
          : "Deleted";

  const items = Array.isArray(questPackDetails.items) ? questPackDetails.items : [];

  return (
    <div className="d-grid gap-3">
      <div className="d-flex align-items-start gap-3 flex-wrap">
        {questPackDetails.imageurl ? (
          <img
            src={questPackDetails.imageurl}
            alt={questPackDetails.name}
            style={{ width: 160, aspectRatio: "16 / 9", objectFit: "cover", borderRadius: 8 }}
          />
        ) : null}

        <div className="flex-grow-1">
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <tbody>
            
                <tr>
                  <th style={{ width: 180 }}>Quest Pack Name</th>
                  <td>{questPackDetails.name || "-"}</td>
                </tr>
                <tr>
                  <th>Description</th>
                  <td><RichTextDisplay html={questPackDetails.description} /></td>
                </tr>
                <tr>
                  <th>Collections</th>
                  <td>{getDisplayNames(questPackDetails.category_names, questPackDetails.category_name || "-")}</td>
                </tr>
                <tr>
                  <th>Sub-category</th>
                  <td>{getDisplayNames(questPackDetails.subcategory_names, questPackDetails.subcategory_name || "-")}</td>
                </tr>
                <tr>
                  <th>Price</th>
                  <td>{questPackDetails.price ?? "-"}</td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td>
                    <span
                      className={`badge ${
                        Number(questPackDetails.status) === 1
                          ? "bg-success"
                          : Number(questPackDetails.status) === 3
                            ? "bg-warning text-dark"
                            : "bg-secondary"
                      }`}
                    >
                      {statusLabel}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>Apple Product ID</th>
                  <td>{questPackDetails.apple_productid || "-"}</td>
                </tr>
                <tr>
                  <th>Android Product ID</th>
                  <td>{questPackDetails.android_productid || "-"}</td>
                </tr>
             
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div>
        <h6 className="mb-3">Challenges ({items.length})</h6>
        {items.length ? (
          <div className="table-responsive">
            <table className="table table-bordered table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th className="text-center">Image</th>
                  <th>Challenge Name</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={`quest-pack-item-${item.id}`}>
                    <td className="text-center" style={{ width: 90 }}>
                      {item.imageurl ? (
                        <img
                          src={item.imageurl}
                          alt={item.itemname}
                          style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8 }}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{item.itemname || "-"}</td>
                    <td><RichTextDisplay html={item.description} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-muted">No challenges found.</div>
        )}
      </div>
    </div>
  );
};

const QuestPacks = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { listData, totalCount, isLoad } = useSelector(
    (state) => state.questPack,
  );
  const { allCategory } = useSelector((state) => state.category);
  const { allSubCatagories } = useSelector((state) => state.subcategory);
  const [searchText, setSearchText] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [questPackDetails, setQuestPackDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [modalMode, setModalMode] = useState(null);

  useEffect(() => {
    dispatch(setQuestPackRedirect(false));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllCategories());
  }, [dispatch]);

  useEffect(() => {
    if (category) {
      dispatch(fetchSubCategoryDataByCatagoryId({ category_id: category }));
    }
  }, [category, dispatch]);

  useEffect(() => {
    setOffset(0);
  }, [searchText, category, subCategory, status, limit]);

  useEffect(() => {
    const normalizedSearchText = searchText.trim();

    dispatch(
      fetchQuestPackList({
        searchText: normalizedSearchText,
        category_ids: category ? [Number(category)] : undefined,
        subcategory_ids: subCategory ? [Number(subCategory)] : undefined,
        status: status || undefined,
        limit,
        offset,
      }),
    );
  }, [dispatch, limit, offset, searchText, category, subCategory, status]);

  const openDeleteModal = (questPack) => {
    setModalMode("delete");
    dispatch(setDeleteQuestPackId(questPack.id));
    dispatch(setTitle("Delete Quest Pack"));
    dispatch(basicModalShowHide());
  };

  const openDetailsModal = async (questPack) => {
    setModalMode("details");
    setQuestPackDetails(null);
    setDetailsLoading(true);

    try {
      const response = await questPackDetailsById({ id: questPack.id });
      if (!response?.success) {
        throw new Error(response?.mesasge || response?.message || "Failed to fetch quest pack details");
      }

      setQuestPackDetails(response.result || null);
      dispatch(setTitle(`Quest Pack Details`));
      dispatch(largeModalShowHide());
    } catch (err) {
      errorToastify(err?.response?.data?.message || err?.message || "Failed to fetch quest pack details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const goToCreate = () => {
    dispatch(setSelectedQuestPack(null));
    navigate("/add-quest-pack");
  };

  const goToEdit = (questPack) => {
    dispatch(setSelectedQuestPack(questPack));
    navigate("/edit-quest-pack", { state: { id: questPack.id, questPack } });
  };

  const toggleStatus = (questPack) => {
    dispatch(
      changeQuestPackStatus({
        id: questPack.id,
        status: Number(questPack.status) === 1 ? 0 : 1,
      }),
    );
  };

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / limit));
  const canGoPrev = offset > 0;
  const canGoNext = offset + limit < totalCount;

  const substringValue = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).substring(0, 100);
  };

  return (
    <div id="main" className="main">
      <Breadcrumb page="Quest Packs" />
      <section className="section">
        <div className="row mb-3">
          <div className="col-sm-6 col-md-12 col-lg-12 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2 item_list">
              <h6 className="card-title new-card-title m-0 p-0">Quest Pack List</h6>
              <input
                className="form-control"
                placeholder="Search quest pack"
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <select
                className="form-select form-select-sm"
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setSubCategory("");
                }}
              >
                <option value="">Select Collections</option>
                {Array.isArray(allCategory) &&
                  allCategory.map((item) => (
                    <option key={item.id} value={item.id}>
                      {getCategoryLabel(item)}
                    </option>
                  ))}
              </select>
              <select
                className="form-select form-select-sm"
                value={subCategory}
                disabled={!category}
                onChange={(e) => setSubCategory(e.target.value)}
              >
                <option value="">Select Sub-category</option>
                {Array.isArray(allSubCatagories) &&
                  allSubCatagories.map((item) => (
                    <option key={item.id} value={item.id}>
                      {getSubCategoryLabel(item)}
                    </option>
                  ))}
              </select>
              <select
                className="form-select form-select-sm"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {can("/Quest", "create") && (
              <Button
                className="btn btn-sm btn-outline-primary me-2"
                title="Add Quest Pack"
                onClick={goToCreate}
              >
                <i className="fa-solid fa-plus" />
              </Button>
            )}
          </div>
        </div>

        <table className="table table-bordered">
          <thead>
            <tr>
              <th className="text-center fs-7">Image</th>
              <th className="text-center fs-7">Quest Pack Name</th>
              <th className="text-center fs-7">Collections</th>
              <th className="text-center fs-7">Description</th>
              <th className="text-center fs-7">Challenges</th>
              <th className="text-center fs-7">Price</th>
              <th className="text-center fs-7">Status</th>
              <th className="text-center fs-7">Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoad ? (
              <tr>
                <td colSpan="8" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : listData?.length ? (
              listData.map((questPack) => (
                <tr key={`quest-pack-${questPack.id}`}>
                  <td className="text-center fs-7">
                    {questPack.imageurl ? (
                      <img
                        src={questPack.imageurl}
                        alt={questPack.name}
                        style={{
                          width: 64,
                          height: 64,
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="text-center fs-7">{questPack.name || "-"}</td>
                  <td className="text-center fs-7">
                    {getDisplayNames(questPack.category_names, questPack.category_name || "-")}
                  </td>
                  <td className="text-center fs-7">
                    {substringValue(stripHtml(questPack.description)) || "-"}
                  </td>
                  <td className="text-center fs-7">
                    {Array.isArray(questPack.items)
                      ? questPack.items.length
                      : 0}
                  </td>
                  <td className="text-center fs-7">{questPack.price ?? 0}</td>
                  <td className="text-center fs-7">
                    <PackStatusBadge status={questPack.status} />
                  </td>
                  <td className="text-center fs-7">
                    <RowActions>
                      <RowActionButton
                        label="View quest pack"
                        iconClass={RowActionIcons.view}
                        onClick={() => openDetailsModal(questPack)}
                      />
                      {can("/Quest", "update") && (
                        <RowActionButton
                          label="Edit quest pack"
                          iconClass={RowActionIcons.edit}
                          variant="outline-primary"
                          onClick={() => goToEdit(questPack)}
                        />
                      )}
                      {can("/Quest", "create") && (
                        <RowActionButton
                          label={
                            Number(questPack.status) === 1
                              ? "Unpublish quest pack"
                              : "Publish quest pack"
                          }
                          iconClass={
                            Number(questPack.status) === 1
                              ? RowActionIcons.unpublish
                              : RowActionIcons.publish
                          }
                          variant={
                            Number(questPack.status) === 1
                              ? "outline-danger"
                              : "outline-success"
                          }
                          onClick={() => toggleStatus(questPack)}
                        />
                      )}
                      {can("/Quest", "delete") && (
                        <RowActionButton
                          label="Delete quest pack"
                          iconClass={RowActionIcons.delete}
                          variant="outline-danger"
                          onClick={() => openDeleteModal(questPack)}
                        />
                      )}
                    </RowActions>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center">
                  No quest packs found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="d-flex justify-content-end align-items-center mt-3 gap-2 flex-wrap">
          <div className="d-inline-flex align-items-center gap-2">
            <span className="fs-7">Show</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 90 }}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              {LIMIT_OPTIONS.map((value) => (
                <option key={`limit-${value}`} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <span className="fs-7">per page</span>
          </div>

          <div className="flex-grow-1" />
          <span className="fs-7 text-muted">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            className="btn btn-outline-primary btn-sm"
            disabled={!canGoPrev}
            onClick={() => setOffset(offset - limit)}
          >
            Prev
          </Button>
          <Button
            className="btn btn-outline-primary btn-sm"
            disabled={!canGoNext}
            onClick={() => setOffset(offset + limit)}
          >
            Next
          </Button>
        </div>
      </section>

      {modalMode === "delete" ? (
        <BasicModal>
          <QuestPackDelete />
        </BasicModal>
      ) : (
        <LargeModal>
          <QuestPackDetailsModal questPackDetails={questPackDetails} isLoading={detailsLoading} />
        </LargeModal>
      )}
    </div>
  );
};

export default QuestPacks;
